import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import i18n from 'i18n';
import { ActorSubjectMetadataMessage } from '@/types/ActorSubjectMetadata';
import { fetchActorSubject } from 'api/ActorSubject';
import { LOGIN, LOGIN_CONSENT, TOS, PRIVACY } from 'routes';

interface ActorContextValue {
  actor?: ActorSubjectMetadataMessage;
  setActor: (actor?: ActorSubjectMetadataMessage) => void;
}

const ActorContext = createContext<ActorContextValue>({
  actor: undefined,
  setActor: () => {},
});
/**
 * Provider that fetches and stores the current actor subject metadata.
 * Blocks rendering of routes until the initial actor fetch completes for non-public pages.
 */
export const ActorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [actor, setActor] = useState<ActorSubjectMetadataMessage | undefined>(undefined);
  const location = useLocation();
  // Determine if running in test environment
  const isTest = process.env.NODE_ENV === 'test';
  // Paths where actor fetch should be skipped (no auth redirect)
  const skipPaths = [LOGIN, LOGIN_CONSENT, TOS, PRIVACY];
  // Initial loading: skip blocking for test env or designated public/legal/login pages
  const initialLoading = !isTest && !skipPaths.includes(location.pathname);
  const [loadingActor, setLoadingActor] = useState<boolean>(initialLoading);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  useEffect(() => {
    const path = location.pathname;
    // Skip fetch on test env or designated public/legal/login pages
    if (isTest || skipPaths.includes(path)) {
      return;
    }
    // Fetch actor once on first non-skip route
    if (!hasFetched) {
      setHasFetched(true);
      setLoadingActor(true);
      fetchActorSubject()
        .then(resp => {
          if (resp.actorMetadata) {
            setActor(resp.actorMetadata);
            // Set UI language to actor preference
            const lang = resp.actorMetadata.actorLingo;
            if (lang) {
              i18n.changeLanguage(lang);
            }
          }
        })
        .catch(() => {
          // ignore errors (not logged in or not consented)
        })
        .finally(() => {
          setLoadingActor(false);
        });
    }
  }, [location.pathname, hasFetched]);

  // Do not render children until actor fetch completes for non-public pages
  if (loadingActor) {
    return null;
  }

  return (
    <ActorContext.Provider value={{ actor, setActor }}>
      {children}
    </ActorContext.Provider>
  );
};

/**
 * Hook to access current actor subject metadata.
 */
export const useActor = (): ActorContextValue => useContext(ActorContext);